import { Image as PDFImage } from '@react-pdf/renderer';
import cloudinary from 'cloudinary-core';
import React from 'react';
import { Box } from '.';

export default function Image({
  src,
  height,
  width,
  crop = 'pad',
  quality = 'auto',
  fetch_format = 'jpg',
  // Set the display resolution to 4 to ensure print resolution.
  dpr = '4',
  trim = false,
  effect = undefined,
  gravity = undefined,
  style = {},
  ...rest
}) {
  if (!src) return null;
  // Currently, the sourceUrl on the GraphQL doesn't return a string.
  // Therefore, we need to extract the image details from the query
  // result and re-calculate the Cloudinary URL here.
  //
  // The ternary that handles cdn.bottlebooks.me routes can be removed
  // after Berkmann and Justerinis templates are migrated to GraphQL
  let imageId = src;
  if (imageId.indexOf('/v1/bottlebooks/') > -1)
    imageId = src.match(/v1\/bottlebooks\/(.+)/)[1];
  if (imageId.indexOf('/v1/api-large/') > -1)
    imageId = src.match(/v1\/api-large\/(.+)/)[1];
  imageId =
    imageId.indexOf('cdn.bottlebooks.me') > -1
      ? imageId
          .replace('http:', '')
          .replace('https:', '')
          .replace('//cdn.bottlebooks.me/', '')
      : imageId;

  let transformation = [];
  // trim:20 to nudge it a little and ensure image is trimmed
  // Otherwise, the image might not be trimmed, or only trimmed on one side
  // We may need to make this configurable, because some logos may be fine enough to have portions removed by trim:20
  // https://res.cloudinary.com/bottlebooks/image/upload/e_trim:0/v1/bottlebooks/57a1f36dd5d44bac0b730bac/Bolney_Wine_Estate-2016_Bolney_Blanc_de_Blanc-bottleImage-Blanc_de_Blancs_2016_no_BG-4362.jpg
  // https://res.cloudinary.com/bottlebooks/image/upload/e_trim:10/v1/bottlebooks/57a1f36dd5d44bac0b730bac/Bolney_Wine_Estate-2016_Bolney_Blanc_de_Blanc-bottleImage-Blanc_de_Blancs_2016_no_BG-4362.jpg
  // https://res.cloudinary.com/bottlebooks/image/upload/e_trim:20/v1/bottlebooks/57a1f36dd5d44bac0b730bac/Bolney_Wine_Estate-2016_Bolney_Blanc_de_Blanc-bottleImage-Blanc_de_Blancs_2016_no_BG-4362.jpg
  if (trim) transformation.push({ effect: 'trim:20' });
  if (effect) transformation.push({ effect });
  if (height || width)
    transformation.push({
      height,
      width,
      crop,
      dpr,
      quality,
      fetch_format,
      gravity,
    });

  // Create your instance
  const cl = cloudinary.Cloudinary.new();
  cl.config('cloud_name', 'bottlebooks');
  const imageURL = cl.url('bottlebooks/' + imageId, {
    transformation,
  });
  // We need to limit the size of the initial image or else transformations won't run
  // We can't use the Cloudinary transformation interface, because it doesn't support chaining of height. It only takes the last height.
  // Therefore we have to hack the URL.
  // We limit the image to 3600 because this is A4 at 300 dpi, which is more than enough for most PDFs
  // This of course costs us an additional transformation, but this is a one-time cost for each PDF.
  const safeImageUrl = imageURL.replace('upload/', 'upload/h_3600,c_limit/');
  return (
    <StyledImage
      src={safeImageUrl}
      width={width}
      height={height}
      style={style}
      {...rest}
    />
  );
}

function StyledImage({ src, width, height, style, ...rest }) {
  return (
    // An image needs to be wrapped in a Box in order to behave like in a web BrowserRouter.
    // Otherwise, the image is distorted to fix the size of the parent component.
    <Box style={[style, { height, width }]} {...rest}>
      <PDFImage src={src} />
    </Box>
  );
}
